import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  Slide,
  Drawer,
  ThemeProvider,
  Stack,
} from '@mui/material';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { MapProvider } from 'react-map-gl';
import { getPath } from 'handimap-react-lib/api/handimapApi';

import './App.css';
import theme from './theme';
import i18n from './utils/i18n';

import MapZone from 'handimap-react-lib/containers/MapZone';
import { LegendContentControl } from 'handimap-react-lib/containers/LegendContent';
import { SettingLayers } from 'handimap-react-lib/containers/SettingLayers';
import { SideBarContent } from './containers/SideBarContent';
import { useResponsive } from 'handimap-react-lib/hooks/useResponsive';
import { LayersButtonsSection } from 'handimap-react-lib/components/LayersButtonsSection';
import { AlertNoti } from 'handimap-react-lib/components/Alert/AlertNoti';
import {
  setOpenDrawer,
  setOpenSideBar,
} from 'handimap-react-lib/store/actions/sideBarActions';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { LanguageSelector } from './components/LanguageSelector';
import { LayersEngine } from 'handimap-react-lib/components/LayersEngine';
import { MaptypeContainer } from 'handimap-react-lib/containers/MaptypeContainer';
import { ModalZone } from 'handimap-react-lib/components/ModalZone';
import { DataModal } from './components/DataModal';
import { callGetJson } from 'handimap-react-lib/api/jsonConfApi';
import { openModal } from 'handimap-react-lib/store/actions/modalActions';
import { ZoneSection } from 'handimap-react-lib/containers/ZoneSection';
import { baseMaps, extras } from './baseMap';
import { setLegendsPerma } from 'handimap-react-lib/store/actions/legendActions';
import { graphByProfile } from './utils/graphConverter';
import { FeaturesListButton } from 'handimap-react-lib/components/PoiListingMenuComponents/FeaturesListButton';
import { PoiListingMenu } from './containers/PoiListingMenu';
import { HandleAlertNotiItinerary } from 'handimap-react-lib/components/Alert/HandleAlertNotiItinerary';
import { CreditSideBar } from 'handimap-react-lib/components/CreditSideBar';
import { convertProfiltForApi } from './utils/profilConverter';
import { ZonesLayer } from './components/ZonesLayer';
import { pathLayerAsLine } from './enum/mapStylesPath';
import { UrlEngine } from 'handimap-react-lib/components/UrlEngine';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='right' ref={ref} {...props} />;
});

const TransitionRight = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='left' ref={ref} {...props} />;
});

export const LayerModal = ({ isMobile, open }) => {
  /**
   * Accessibility infos: https://www.w3.org/WAI/ARIA/apg/patterns/dialogmodal/
   *                      https://www.accede-web.com/notices/interface-riche/fenetres-modales/
   */

  const { palette } = theme;
  const styles = {
    buttonSetting: {
      minHeight: 0,
      minWidth: 0,
      width: isMobile ? '1.8em' : 'auto',
      height: isMobile ? '1.8em' : 'auto',
      borderRadius: '0.6em',
      border: '0.08em solid',
      fontSize: isMobile ? '1.7em' : '1em',
      backgroundColor: palette.primary.contrastBackground,
      color: palette.primary.main,
      borderColor: palette.primary.main,
      fontStyle: 'normal',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
      },
    },
  };
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return (
    <>
      {!open && (
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            zIndex: '100',
            bottom: isMobile ? null : 40,
            top: isMobile ? '7.5em' : null,
            right: isMobile ? 8 : 10,
          }}
        >
          {isMobile ? (
            <Button
              aria-label={t('layer.aria-open')}
              onClick={() => dispatch(setOpenDrawer(true))}
              sx={styles.buttonSetting}
              size='medium'
              variant='outlined'
            >
              <FontAwesomeIcon size='sm' icon={faLayerGroup} />
            </Button>
          ) : (
            <Button
              onClick={() => dispatch(setOpenDrawer(true))}
              sx={styles.buttonSetting}
              size='small'
              variant='outlined'
              startIcon={<FontAwesomeIcon icon={faLayerGroup} />}
            >
              {t('layer.open')}
            </Button>
          )}
        </Box>
      )}
      {isMobile ? (
        <Dialog
          aria-labelledby='modal-zone-title'
          role='dialog'
          aria-modal='true'
          fullScreen
          open={open}
          onClose={() => dispatch(setOpenDrawer(false))}
          TransitionComponent={TransitionRight}
        >
          <SettingLayers
            onClose={() => dispatch(setOpenDrawer(false))}
            isMobile={isMobile}
            convertProfiltForApi={(profil, condition) =>
              convertProfiltForApi(profil, condition)
            }
          />
        </Dialog>
      ) : (
        <Drawer
          aria-labelledby='modal-zone-title'
          role='dialog'
          aria-modal='true'
          anchor='right'
          open={open}
          onClose={() => dispatch(setOpenDrawer(false))}
        >
          <SettingLayers
            onClose={() => dispatch(setOpenDrawer(false))}
            isMobile={isMobile}
            convertProfiltForApi={(profil, condition) =>
              convertProfiltForApi(profil, condition)
            }
          />
        </Drawer>
      )}
    </>
  );
};

function App() {
  const { isMobile } = useResponsive();

  const showDirections = useSelector((state) => state.sideBar.open);
  const showDrawer = useSelector((state) => state.sideBar.drawer);
  const showAlert = useSelector((state) => state.alert.isOpen);
  const alertContent = useSelector((state) => state.alert.content);
  const alertSeverity = useSelector((state) => state.alert.severity);
  const alertTitle = useSelector((state) => state.alert.title);
  const alertRole = useSelector((state) => state.alert.role);
  const profil = useSelector((state) => state.profils.profil);
  const poiLinstingMenuState = useSelector(
    (state) => state.poiListingMenuReducer.open,
  );

  const [baseProfile, setBaseProfile] = useState(graphByProfile(profil));

  const { t } = useTranslation();
  const getLanguage = () => i18n.language || window.localStorage.i18nextLng;
  const dispatch = useDispatch();
  const { palette } = theme;

  const multimodal = useSelector((state) => state.profils.multimodal);
  const apiProfil = convertProfiltForApi(profil);
  const origin = [...useSelector((state) => state.directions.origin.marker)];
  const destination = [
    ...useSelector((state) => state.directions.destination.marker),
  ];

  const { status, allResults, error } = getPath(
    apiProfil,
    multimodal,
    origin,
    destination,
  );

  const styles = {
    sideBar: {
      boxShadow: '0px 5px 5px #888888',
      zIndex: isMobile ? null : '100',
      alignContent: 'center',
      maxWidth: isMobile ? '100%' : '24.25em',
      minWidth: isMobile ? '100vw' : 'max-content',
    },
    buttonMenu: {
      position: 'absolute',
      top: 10,
      left: 10,
      zIndex: '100',
      backgroundColor: palette.primary.main,
      color: palette.primary.contrastText,
      '&:hover': {
        backgroundColor: palette.primary.contrastText,
        color: palette.primary.main,
      },
    },
    map: {
      display: 'flex',
      height: '100%',
      width: '100%',
    },
    buttonSetting: {
      minHeight: 0,
      minWidth: 0,
      padding: 1,
      borderRadius: '0.5em',
      fontSize: '0.875em',
      backgroundColor: palette.primary.contrastBackground,
      color: palette.primary.main,
      borderColor: palette.primary.main,
      fontStyle: 'normal',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
      },
    },
  };

  // handle url params to fill origin and destination inputs
  // origin=-3.380118759727253,47.75755855083423&destination=-3.365463687506235,47.748446408159424&profil=Déficience motrice&condition=Fauteuil roulant manuel&multimodal=false&selectedPath=1
  const queryParams = new URLSearchParams(window.location.search);
  const originFromUrl = queryParams.get('origin');
  const destinationFromUrl = queryParams.get('destination');
  const profileFromUrl = queryParams.get('profil');
  const conditionFromUrl = queryParams.get('condition');
  const multimodalFromUrl = queryParams.get('multimodal');
  const selectedPathFromUrl = queryParams.get('selectedPath');
  const zoneFromUrl = queryParams.get('zone');
  useEffect(() => {
    if (
      zoneFromUrl !== null &&
      zoneFromUrl !== undefined &&
      zoneFromUrl !== ''
    ) {
      callGetJson('custom_zoom_zone_button.json').then((result) => {
        const zoomByZone = Object.assign(
          {},
          ...result.map((x) => ({ [x.zone]: x })),
        );
        let zoneDes = zoomByZone[zoneFromUrl].textFrDesc;
        let imageDes = zoomByZone[zoneFromUrl].imageFRDesc;
        if (getLanguage() === 'fr') {
          zoneDes = zoomByZone[zoneFromUrl].textFrDesc;
          imageDes = zoomByZone[zoneFromUrl].imageFRDesc;
        } else if (getLanguage() === 'en') {
          zoneDes = zoomByZone[zoneFromUrl].textEnDesc;
          imageDes = zoomByZone[zoneFromUrl].imageENDesc;
        } else if (getLanguage() === 'es') {
          zoneDes = zoomByZone[zoneFromUrl].textEsDesc;
          imageDes = zoomByZone[zoneFromUrl].imageESDesc;
        }
        const zoneImage = zoomByZone[zoneFromUrl].image;
        const credits = zoomByZone[zoneFromUrl].credits;
        dispatch(openModal(zoneFromUrl, zoneDes, zoneImage, credits, imageDes));
      });
    } else {
      callGetJson('custom_info_city.json').then((result) => {
        dispatch(
          openModal(
            `${t('common.welcome')} ${result.city}`,
            result['app-present'],
          ),
        );
      });
    }
    dispatch(setLegendsPerma(['acc', 'part_acc', 'not_acc', 'not_spec']));
    /**
     * window.history.replaceState remove url params
     */
    window.history.replaceState({}, '', '/handimap');

    document.documentElement.lang = getLanguage();
  }, []);

  useEffect(() => {
    setBaseProfile(graphByProfile(profil));
  }, [profil]);

  return (
    <ThemeProvider theme={theme}>
      <I18nextProvider i18n={i18n}>
        <div className='App' style={{ display: 'flex' }}>
          <MapProvider>
            <LayersEngine isMobile={isMobile} />
            <UrlEngine
              origin={originFromUrl}
              destination={destinationFromUrl}
              profil={profileFromUrl}
              condition={conditionFromUrl}
              multimodal={multimodalFromUrl}
              selectedPath={selectedPathFromUrl}
            />
            <HandleAlertNotiItinerary
              status={status}
              allResults={allResults}
              error={error}
            />
            {isMobile ? (
              <>
                <ModalZone color={palette.primary.main} />
                <DataModal color={palette.primary.main} />
                <Dialog
                  fullScreen
                  open={showDirections}
                  onClose={() => dispatch(setOpenSideBar(false))}
                  TransitionComponent={Transition}
                >
                  <Box
                    sx={{
                      backgroundColor: palette.primary.main,
                      display: 'flex',
                      width: '100%',
                      direction: 'ltr',
                    }}
                  >
                    <SideBarContent
                      status={status}
                      allResults={allResults}
                      isMobile={isMobile}
                      convertProfiltForApi={(profil, condition) =>
                        convertProfiltForApi(profil, condition)
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      height: '100%',
                      alignItems: 'flex-end',
                      backgroundColor: palette.primary.main,
                    }}
                  >
                    <CreditSideBar />
                  </Box>
                </Dialog>
                <Drawer
                  open={poiLinstingMenuState}
                  onClose={() => dispatch(setOpenSideBar(false))}
                  anchor='right'
                  variant='persistent'
                >
                  <PoiListingMenu isMobile={isMobile} />
                  {showAlert && (
                    <Box
                      sx={{
                        display: 'flex',
                        position: 'fixed',
                        left: '50%',
                        top: '15%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        zIndex: 110,
                      }}
                    >
                      <AlertNoti
                        content={alertContent}
                        severity={alertSeverity}
                        title={alertTitle}
                        role={alertRole}
                      />
                    </Box>
                  )}
                </Drawer>
                <>
                  <div
                    className='map-bar'
                    aria-labelledby='Carte'
                    style={styles.map}
                  >
                    <MapZone
                      baseMaps={baseMaps}
                      extrasLayers={extras}
                      baseGraph={baseProfile}
                      zoneFromUrl={zoneFromUrl}
                      isMobile={isMobile}
                      hiddenButtons={poiLinstingMenuState}
                      convertProfiltForApi={(profil, condition) =>
                        convertProfiltForApi(profil, condition)
                      }
                      status={status}
                      allResults={allResults}
                    >
                      <ZonesLayer></ZonesLayer>
                    </MapZone>
                    {showAlert && (
                      <Box
                        sx={{
                          display: 'flex',
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '80%',
                        }}
                      >
                        <AlertNoti
                          content={alertContent}
                          severity={alertSeverity}
                          title={alertTitle}
                          role={alertRole}
                        />
                      </Box>
                    )}
                    <div hidden={poiLinstingMenuState}>
                      <Button
                        aria-label={t('common.aria-open-side-mobile')}
                        sx={styles.buttonMenu}
                        onClick={() => dispatch(setOpenSideBar(true))}
                      >
                        {t('common.open-side-mobile')}
                      </Button>
                      <LayerModal open={showDrawer} isMobile={isMobile} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '11em',
                          right: 8,
                          zIndex: 100,
                        }}
                      >
                        <FeaturesListButton />
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: '3em',
                          left: 10,
                          zIndex: 100,
                        }}
                      >
                        <MaptypeContainer />
                      </Box>
                      <Box
                        sx={{
                          minWidth: '8em',
                          position: 'absolute',
                          zIndex: '100',
                          bottom: '3.5em',
                          right: 8,
                        }}
                      >
                        <ZoneSection />
                      </Box>
                    </div>
                  </div>
                </>
              </>
            ) : (
              <>
                <ModalZone color={palette.primary.main} />
                <DataModal color={palette.primary.main} />
                <Drawer
                  sx={{
                    backgroundColor: palette.primary.main,
                    width: isMobile ? '100%' : '390px',
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                      width: isMobile ? '100%' : '390px',
                      boxSizing: 'border-box',
                      backgroundColor: palette.primary.main,
                      '&:focus': {
                        outline: 'none',
                      },
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: '0.5em',
                        backgroundColor: 'gray',
                      },
                    },
                  }}
                  variant='permanent'
                  anchor='left'
                >
                  <SideBarContent
                    status={status}
                    allResults={allResults}
                    isMobile={isMobile}
                    convertProfiltForApi={(profil, condition) =>
                      convertProfiltForApi(profil, condition)
                    }
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      height: '100%',
                      alignItems: 'flex-end',
                    }}
                  >
                    <CreditSideBar />
                  </Box>
                </Drawer>
                <div
                  className='map-bar'
                  style={{ display: 'flex', overflow: 'hidden' }}
                >
                  <Drawer
                    id='poi-disclosure'
                    sx={{
                      '& .MuiDrawer-paper': {
                        width: 'calc(100vw - 390px)',
                        boxSizing: 'border-box',
                        backgroundColor: palette.primary.main,
                      },
                    }}
                    open={poiLinstingMenuState}
                    anchor='right'
                    variant='persistent'
                  >
                    <PoiListingMenu isMobile={isMobile} />
                  </Drawer>

                  <>
                    <MapZone
                      stylePath={pathLayerAsLine}
                      baseMaps={baseMaps}
                      extrasLayers={extras}
                      baseGraph={baseProfile}
                      isMobile={isMobile}
                      originFromUrl={originFromUrl}
                      destinationFromUrl={destinationFromUrl}
                      zoneFromUrl={zoneFromUrl}
                      hiddenButtons={poiLinstingMenuState}
                      convertProfiltForApi={(profil, condition) =>
                        convertProfiltForApi(profil, condition)
                      }
                      status={status}
                      allResults={allResults}
                    >
                      <ZonesLayer></ZonesLayer>
                    </MapZone>
                    <div hidden={poiLinstingMenuState}>
                      <Box
                        sx={{
                          position: 'absolute',
                          minWidth: '8em',
                          top: '0.5em',
                          left: 450,
                        }}
                      >
                        <ZoneSection />
                      </Box>
                      <Stack
                        sx={{ position: 'absolute', top: '0.5em', right: 5 }}
                        alignItems='flex-end'
                        direction='column'
                        spacing={2}
                      >
                        <LanguageSelector />
                        <MaptypeContainer />
                        <FeaturesListButton />
                      </Stack>
                      <Box
                        sx={{
                          display: 'flex',
                          position: 'absolute',
                          left: '400px',
                          bottom: 30,
                        }}
                      >
                        <Stack direction='column' space={10}>
                          <LegendContentControl
                            isMobile={isMobile}
                            allResults={allResults}
                          />
                          {!showDrawer && (
                            <>
                              <LayersButtonsSection />
                            </>
                          )}
                        </Stack>
                      </Box>
                      <Box>
                        <LayerModal open={showDrawer} isMobile={isMobile} />
                      </Box>
                    </div>
                  </>

                  {showAlert && (
                    // zindex=1201 because the drawer zindex=1200 and we need notif when drawer is opened
                    <Box
                      sx={{
                        display: 'flex',
                        position: 'fixed',
                        left: '50%',
                        transform: 'translate(-18%, 0);',
                        top: '1em',
                        zIndex: 1201,
                      }}
                    >
                      <AlertNoti
                        content={alertContent}
                        severity={alertSeverity}
                        title={alertTitle}
                        role={alertRole}
                      />
                    </Box>
                  )}
                </div>
              </>
            )}
          </MapProvider>
        </div>
      </I18nextProvider>
    </ThemeProvider>
  );
}

export default App;
